import { createAsyncThunk, createSlice, AsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import eventCategoryService from '../../API/eventCategory';
import { RootState } from '../store';

interface EventCategory {
	id: string;
	name: string;
	description: string;
}

interface UpdateEventPayload {
	id: string;
	event: Partial<EventCategory>;
}

interface AsyncThunkConfig {
	state: RootState;
	rejectValue: string;
}

//@ts-ignore
export const asyncRetrieveEventCategories: AsyncThunk<EventCategory[], void, AsyncThunkConfig> =
	createAsyncThunk('eventCategory/retrieve', async (_, thunkApi) => {
		try {
			const res = await eventCategoryService.retrieveEvents();
			return res.data.data;
		} catch (error: any) {
			const errorMessage =
				(error.response && error.response.data && error.response.data.message) ||
				error.message ||
				error.toString();
			toast.error(errorMessage);
			return thunkApi.rejectWithValue(errorMessage);
		}
	});

//@ts-ignore
export const asyncCreateEventCategory: AsyncThunk<EventCategory, EventCategory, AsyncThunkConfig> =
	createAsyncThunk('eventCategory/create', async (values: EventCategory, thunkApi) => {
		try {
			const res = await eventCategoryService.createEventCategory(values);
			const successMessage = res.data.message;
			toast.success(successMessage);
			return res.data.data;
		} catch (error: any) {
			const errorMessage =
				(error.response && error.response.data && error.response.data.message) ||
				error.message ||
				error.toString();
			toast.error(errorMessage);
		}
	});

//@ts-ignore
export const asyncUpdateEventCategory: AsyncThunk<
	EventCategory,
	UpdateEventPayload,
	AsyncThunkConfig
> = createAsyncThunk('eventCategory/update', async ({ id, event }, thunkApi) => {
	try {
		const res = await eventCategoryService.updateEventCategory(id, event);
		const successMessage = res.data.message;
		toast.success(successMessage);
		return { id, event };
	} catch (error: any) {
		const errorMessage =
			(error.response && error.response.data && error.response.message) ||
			error.message ||
			error.toString();
		toast.error(errorMessage);
		return thunkApi.rejectWithValue(errorMessage);
	}
});

// @ts-ignore
type DeleteEventPayload = string;
// @ts-ignore
export const asyncDeleteEventCategory: AsyncThunk<
	EventCategory,
	DeleteEventPayload,
	AsyncThunkConfig
> = createAsyncThunk('eventCategory/delete', async (id: DeleteEventPayload, thunkApi) => {
	try {
		const res = await eventCategoryService.deleteEventCategory(id);
		const successMessage = res.data.message;

		toast.success(successMessage);
		return res.data.data as EventCategory;
	} catch (error: any) {
		const errorMessage =
			(error.response && error.response.data && error.response.message) ||
			error.message ||
			error.toString();

		toast.error(errorMessage);
		return thunkApi.rejectWithValue(errorMessage);
	}
});

const eventCategorySlice = createSlice({
	name: 'eventCategory',
	initialState: {
		data: [] as EventCategory[],
		isLoading: false,
	},
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(asyncRetrieveEventCategories.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(asyncRetrieveEventCategories.fulfilled, (state, action) => {
				state.isLoading = false;
				state.data = action.payload;
			})
			.addCase(asyncRetrieveEventCategories.rejected, (state) => {
				state.isLoading = false;
			})
			.addCase(asyncCreateEventCategory.fulfilled, (state, action) => {
				state.isLoading = false;
				state.data = [action.payload, ...state.data];
			})
			.addCase(asyncCreateEventCategory.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(asyncCreateEventCategory.rejected, (state) => {
				state.isLoading = false;
			})
			.addCase(asyncUpdateEventCategory.fulfilled, (state, action) => {
				//   @ts-ignore
				const { id, event } = action.payload;
				const updatedState = state.data.map((item) =>
					item.id === id ? { ...item, ...event } : item,
				);
				state.data = updatedState;
			})
			.addCase(asyncUpdateEventCategory.rejected, (state, action) => {
				state.isLoading = false;
			})
			.addCase(asyncDeleteEventCategory.fulfilled, (state, action) => {
				state.data = state.data.filter(
					// @ts-ignore
					(category) => category.id !== action.meta.arg,
				);
			});
	},
});

export const allEventCategories = (state: RootState) => state.eventCategory;
export default eventCategorySlice.reducer;
